// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

type RetryFields = { restaurant_name: string; restaurant_location: string; title?: string };

interface Props {
  isOpen: boolean;
  url: string;
  errorMessage?: string;
  onCancel: () => void;
  onRetry: (fields: RetryFields) => Promise<void> | void;
  /** i18n hooks */
  labels?: {
    heading?: string;
    reason?: string;
    instructions?: string;
    name?: string;
    location?: string;
    title?: string;
    cancel?: string;
    retry?: string;
    retrying?: string;
  };
}

const defaultLabels = {
  heading: "No pudimos extraer datos del video",
  reason: "Motivo",
  instructions:
    "Ingresa el nombre del restaurante y su ubicación para intentar enriquecer la ficha con Google Places.",
  name: "Nombre del restaurante",
  location: "Ubicación (ciudad/zona)",
  title: "Título (opcional)",
  cancel: "Cancelar",
  retry: "Reintentar enriquecimiento",
  retrying: "Reintentando…",
};

function useScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");
  return Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter(
    (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && !el.getAttribute("aria-hidden")
  );
}

const ExtractionFixDialog: React.FC<Props> = ({
  isOpen,
  url,
  errorMessage,
  onCancel,
  onRetry,
  labels: L = defaultLabels,
}) => {
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantLocation, setRestaurantLocation] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [locError, setLocError] = useState<string | null>(null);

  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useScrollLock(isOpen);

  // Reset / autofocus on open
  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setNameError(null);
      setLocError(null);
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    } else {
      setRestaurantName("");
      setRestaurantLocation("");
      setTitle("");
      setSubmitting(false);
    }
  }, [isOpen]);

  // Focus trap + Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (!submitting) onCancel();
        return;
      }
      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const els = getFocusable(panel);
        if (els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onCancel]);

  const nameId = "fix-name";
  const locId = "fix-location";
  const headingId = "fix-heading";
  const descId = "fix-desc";
  const nameErrId = "fix-name-err";
  const locErrId = "fix-location-err";

  const validate = () => {
    const n = restaurantName.trim();
    const l = restaurantLocation.trim();
    let ok = true;
    if (n.length < 2) {
      setNameError("Escribe al menos 2 caracteres.");
      ok = false;
    } else if (n.length > 80) {
      setNameError("Máximo 80 caracteres.");
      ok = false;
    } else {
      setNameError(null);
    }
    if (l.length < 2) {
      setLocError("Escribe una ciudad o zona.");
      ok = false;
    } else if (l.length > 80) {
      setLocError("Máximo 80 caracteres.");
      ok = false;
    } else {
      setLocError(null);
    }
    return ok;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onRetry({
        restaurant_name: restaurantName.trim(),
        restaurant_location: restaurantLocation.trim(),
        title: title.trim() || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          aria-describedby={descId}
        >
          {/* Backdrop (click to close only when not submitting) */}
          <div
            className="absolute inset-0 bg-black/40"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !submitting) onCancel();
            }}
            aria-hidden="true"
          />
          <motion.div
            ref={panelRef}
            className="relative mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl outline-none"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            tabIndex={-1}
          >
            <h3 id={headingId} className="text-lg font-bold text-gray-900">
              {L.heading}
            </h3>

            <p className="mt-1 break-all text-sm text-gray-600">{url}</p>

            {errorMessage && (
              <p className="mt-2 text-sm text-red-600">
                {L.reason}: {errorMessage}
              </p>
            )}

            <p id={descId} className="mt-3 text-sm text-gray-700">
              {L.instructions}
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-3" noValidate>
              <div>
                <label htmlFor={nameId} className="block text-sm font-medium text-gray-700">
                  {L.name}
                </label>
                <input
                  ref={firstFieldRef}
                  id={nameId}
                  type="text"
                  inputMode="text"
                  autoComplete="organization"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  maxLength={80}
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? nameErrId : undefined}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  placeholder="Ej. Taquería El Buen Sabor"
                  required
                />
                {nameError && (
                  <p id={nameErrId} className="mt-1 text-xs text-rose-600">
                    {nameError}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor={locId} className="block text-sm font-medium text-gray-700">
                  {L.location}
                </label>
                <input
                  id={locId}
                  type="text"
                  inputMode="text"
                  autoComplete="address-level2"
                  value={restaurantLocation}
                  onChange={(e) => setRestaurantLocation(e.target.value)}
                  maxLength={80}
                  aria-invalid={!!locError}
                  aria-describedby={locError ? locErrId : undefined}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  placeholder="Ej. Coyoacán, CDMX"
                  required
                />
                {locError && (
                  <p id={locErrId} className="mt-1 text-xs text-rose-600">
                    {locError}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{L.title}</label>
                <input
                  type="text"
                  inputMode="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  placeholder="Ej. Mejores tacos al pastor"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                >
                  {L.cancel}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? L.retrying : L.retry}
                </button>
              </div>
            </form>

            {/* SR-only live region for async status */}
            <div aria-live="polite" className="sr-only">
              {submitting ? L.retrying : ""}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal avoids parent stacking/overflow issues
  return createPortal(body, document.body);
};

export default ExtractionFixDialog;
